export const COOKIE_NAME = "__Secure-ap_attribution_v1";
export const COOKIE_DOMAIN = "africaspocket.com";
import { MAX_AGE_SECONDS, PARAMETERS, validateTouch } from "./attributionTouch.js";
import { label, hostname } from "./attributionTouch.js";
import { marketingPageCode, isMarketingLocation } from "./attributionPages.js";
import {
  JOURNEY_KEY, GENERATION_COOKIE, GENERATION_KEY, validateGeneration, validateJourney,
  upgradeJourney, mergeJourneys, randomHex, nextObservationId, markIncomplete, nonDirect as isNonDirect,
} from "./attributionJourney.js";
export { MAX_AGE_SECONDS, PARAMETERS, validateTouch } from "./attributionTouch.js";
const INTERNAL_HOST = /(^|\.)africaspocket\.com$/i;
export function validateAttribution(record, now = Date.now()) {
  try {
    if (record?.version === 2) return validateJourney(record, now);
    if (!record || record.version !== 1 || Array.isArray(record)) return null;
    const first_touch = validateTouch(record.first_touch, now);
    const signup_touch = validateTouch(record.signup_touch, now);
    return first_touch || signup_touch ? { version: 1, first_touch, signup_touch } : null;
  } catch { return null; }
}

const belongsTo = (host, domain) => host === domain || host.endsWith("." + domain);
export function inferSource(host) {
  const ai = ["chatgpt.com", "openai.com", "claude.ai", "perplexity.ai",
    "gemini.google.com", "copilot.microsoft.com", "deepseek.com", "grok.com"];
  const assistant = ai.find((domain) => belongsTo(host, domain));
  if (assistant) return { source: assistant, medium: "referral" };
  // Explicit domains avoid attributing google.example.com to Google.
  const search = {
    google: ["google.com", "google.co.ke", "google.co.uk", "google.co.za", "google.com.au", "google.ca", "google.de", "google.fr", "google.co.in"],
    bing: ["bing.com"], yahoo: ["yahoo.com", "yahoo.co.jp"], duckduckgo: ["duckduckgo.com"],
  };
  for (const [source, domains] of Object.entries(search)) {
    if (domains.some((domain) => belongsTo(host, domain))) return { source, medium: "organic" };
  }
  const social = { instagram: ["instagram.com"], facebook: ["facebook.com"],
    linkedin: ["linkedin.com"], tiktok: ["tiktok.com"], x: ["x.com"], twitter: ["twitter.com"] };
  const video = { youtube: ["youtube.com", "youtu.be"], vimeo: ["vimeo.com"] };
  for (const [medium, mapping] of [["social", social], ["video", video]]) {
    for (const [source, domains] of Object.entries(mapping)) {
      if (domains.some((domain) => belongsTo(host, domain))) return { source, medium };
    }
  }
  return { source: host, medium: "referral" };
}

export const SESSION_KEY = "ap_attribution_v1";
export const HANDOFF_PARAMETER = "ap_attribution";
export const MAX_HANDOFF_URL_LENGTH = 8192;
export const OWNED_HOSTS = ["africaspocket.com", "www.africaspocket.com", "invest.africaspocket.com"];

export function isInternalReferrer(referrer, location) {
  try {
    const url = new URL(referrer);
    return url.origin === location.origin || INTERNAL_HOST.test(url.hostname);
  } catch { return false; }
}

export function isAcquisitionPage(pathname) {
  const path = pathname.replace(/\.html$/, "").replace(/\/$/, "") || "/";
  if (path.startsWith("/pay/")) return false;
  if (/(?:^|\/)(?:payment[^/]*|subscription-plan-payment)$/.test(path)) return false;
  if (path.startsWith("/auth/")) return ["/auth/sign-up", "/auth/log-in"].includes(path);
  return !["/401", "/404", "/tst", "/style-guide"].includes(path);
}

export function reconcileAttribution(session, handoff, now = Date.now()) {
  const records = [session, handoff].map((record) => validateAttribution(record, now)).filter(Boolean);
  const journey = records.find(record => record.version === 2);
  if (journey) return mergeJourneys(records, journey.generation, now);
  const first = records.map((record) => record.first_touch).filter(Boolean)
    .sort((a, b) => Date.parse(a.captured_at) - Date.parse(b.captured_at))[0] || null;
  const latest = records.map((record) => record.signup_touch).filter(Boolean);
  const meaningful = latest.filter(isNonDirect);
  const signup = (meaningful.length ? meaningful : latest)
    .sort((a, b) => Date.parse(b.captured_at) - Date.parse(a.captured_at))[0] || null;
  return first || signup ? { version: 1, first_touch: first, signup_touch: signup } : null;
}

export function createAttributionService({
  getDocument = () => globalThis.document,
  getLocation = () => globalThis.location,
  getStorage = () => globalThis.sessionStorage,
  getLocalStorage = () => globalThis.localStorage,
  now = () => Date.now(),
} = {}) {
  const validate = record => validateAttribution(record, now());
  let memory = null;
  let generation = null;
  let activeArrival = null;
  function readCookie(name) {
    try {
      const part = getDocument().cookie.split(";").map(s => s.trim()).find(s => s.startsWith(name + "="));
      return part ? JSON.parse(decodeURIComponent(part.slice(name.length + 1))) : null;
    } catch { return null; }
  }
  function readStored(storage, key) {
    try { return JSON.parse(storage().getItem(key)); } catch { return null; }
  }
  function cookieAllowed() {
    try {
      const location = getLocation();
      return location?.protocol === "https:" && INTERNAL_HOST.test(location.hostname);
    } catch { return false; }
  }
  function cookieSuffix() { return "; Domain=" + COOKIE_DOMAIN + "; Path=/; SameSite=Lax; Secure"; }
  function writeCookie(name, value) {
    try {
      if (!cookieAllowed()) return false;
      const cookie = name + "=" + encodeURIComponent(JSON.stringify(value)) + "; Max-Age=" + MAX_AGE_SECONDS + cookieSuffix();
      if (new TextEncoder().encode(cookie).length > 4096) return false;
      getDocument().cookie = cookie;
      return JSON.stringify(readCookie(name)) === JSON.stringify(value);
    } catch { return false; }
  }
  function store(storage, key, value) {
    try { storage().setItem(key, JSON.stringify(value)); return true; } catch { return false; }
  }
  function remove(storage, key) { try { storage().removeItem(key); } catch { /* Storage is optional. */ } }
  function read() { return validate(readCookie(COOKIE_NAME)); }
  function readSession() { return validate(readStored(getStorage, SESSION_KEY)); }

  function readGeneration() {
    return validateGeneration(cookieAllowed() ? readCookie(GENERATION_COOKIE) : readStored(getLocalStorage, GENERATION_KEY), now());
  }
  function currentGeneration(hint = null) {
    const shared = readGeneration();
    generation = validateGeneration(generation, now());
    if (shared) generation = shared;
    if (!generation) generation = { id: hint?.generation || randomHex(), closed: false, expires_at: now() + MAX_AGE_SECONDS * 1000 };
    return generation;
  }
  function persistGeneration() {
    generation.expires_at = now() + MAX_AGE_SECONDS * 1000;
    if (cookieAllowed()) writeCookie(GENERATION_COOKIE, generation);
    else store(getLocalStorage, GENERATION_KEY, generation);
  }
  function recover(handoff = null) {
    const summary = read();
    const local = validate(readStored(getLocalStorage, JOURNEY_KEY));
    const session = readSession();
    const allowLegacy = !readGeneration();
    const marker = currentGeneration(summary?.version === 2 ? summary : local || session || handoff);
    if (marker.closed) { memory = null; remove(getLocalStorage, JOURNEY_KEY); remove(getStorage, SESSION_KEY); return null; }
    const records = [local, session, memory, handoff].map(validate).filter(r => r && ((allowLegacy && r.version === 1) || r.generation === marker.id));
    if (summary && ((allowLegacy && summary.version === 1) || summary.generation === marker.id)) {
      if (summary.version === 2) {
        const known = records.some(r => r.arrivals?.some(a => a.id === summary.last_observation || a.pages.some(p => p[0] === summary.last_observation)));
        if (known) {
          summary.completeness.reasons = summary.completeness.reasons.filter(r => r !== "summary_only");
          summary.completeness.partial = !!summary.completeness.reasons.length;
        }
      }
      records.push(summary);
    }
    // Old-generation local records are never allowed back after signup/withdrawal.
    if (local?.version === 2 && local.generation !== marker.id) remove(getLocalStorage, JOURNEY_KEY);
    if (session?.version === 2 && session.generation !== marker.id) remove(getStorage, SESSION_KEY);
    memory = mergeJourneys(records, marker.id, now());
    return validate(memory);
  }
  function persist(record) {
    const safe = validate(record);
    if (!safe) return false;
    const marker = currentGeneration(safe);
    if (marker.closed || (safe.version === 2 && safe.generation !== marker.id)) return false;
    memory = upgradeJourney(safe, marker.id);
    // Reconcile a late write from another same-origin tab before writing our copy.
    const latest = validate(readStored(getLocalStorage, JOURNEY_KEY));
    memory = mergeJourneys([latest, memory], marker.id, now()) || memory;
    const localSaved = store(getLocalStorage, JOURNEY_KEY, memory);
    if (!localSaved) markIncomplete(memory, "storage_unavailable");
    store(getStorage, SESSION_KEY, memory);
    persistGeneration();
    const summary = { ...memory, arrivals: [], active_arrival: null,
      completeness: { partial: true, reasons: [...new Set([...memory.completeness.reasons, "summary_only"])].sort() } };
    // A summary remains bounded even with unusually long campaign labels.
    if (writeCookie(COOKIE_NAME, summary)) return true;
    for (const field of ["landing_path", "term", "content", "campaign_id", "campaign", "referrer_host"]) {
      summary.first_touch = summary.first_touch ? { ...summary.first_touch } : null;
      summary.signup_touch = summary.signup_touch ? { ...summary.signup_touch } : null;
      if (summary.first_touch) delete summary.first_touch[field];
      if (summary.signup_touch) delete summary.signup_touch[field];
      if (writeCookie(COOKIE_NAME, summary)) return true;
    }
    return false;
  }
  function clear(reason = "signup") {
    memory = null; activeArrival = null;
    generation = { id: randomHex(), closed: reason === "signup", expires_at: now() + MAX_AGE_SECONDS * 1000 };
    persistGeneration();
    remove(getStorage, SESSION_KEY); remove(getLocalStorage, JOURNEY_KEY);
    try { if (cookieAllowed()) getDocument().cookie = COOKIE_NAME + "=; Max-Age=0" + cookieSuffix(); } catch { /* Optional storage. */ }
  }
  function readHandoff() {
    try {
      const raw = new URLSearchParams(getLocation().search).get(HANDOFF_PARAMETER);
      return raw && raw.length <= MAX_HANDOFF_URL_LENGTH ? validate(JSON.parse(raw)) : null;
    } catch { return null; }
  }

  function currentTouch(existing = null, hasHandoff = false) {
    const location = getLocation();
    const params = new URLSearchParams(location.search);
    const touch = {};
    for (const [parameter, field] of Object.entries(PARAMETERS)) {
      const value = ["source", "medium"].includes(field)
        ? label(params.get(parameter), 100) : params.get(parameter);
      if (value) touch[field] = value;
    }
    const referrer = getDocument().referrer;
    const internal = isInternalReferrer(referrer, location);
    const hasUtm = Object.keys(touch).length > 0;
    // Replayed handoff UTMs are transport, not a newly observed campaign.
    const previous = existing?.signup_touch || existing?.first_touch;
    const repeatsPrevious = previous && Object.entries(touch).every(([key, value]) => previous[key] === value);
    if ((internal || (hasHandoff && !referrer)) && existing && (!hasUtm || repeatsPrevious)) return null;
    let host = null;
    try { if (referrer) host = hostname(new URL(referrer).hostname); } catch { /* Unknown referrer. */ }
    if (host) touch.referrer_host = host;
    if (isMarketingLocation(location)) {
      touch.site = "m";
      touch.page_code = marketingPageCode(location.pathname);
    } else touch.landing_path = location.pathname;
    touch.captured_at = new Date(now()).toISOString();
    if (hasUtm) return validateTouch(touch, now());
    if (internal) return null;
    if (host) return validateTouch({ ...touch, ...inferSource(host) }, now());
    if (referrer) return null;
    return validateTouch({ ...touch, source: "direct", medium: "none" }, now());
  }

  function prepare({ consentGranted, handoff = null, capture = true }) {
    if (consentGranted !== true) return null;
    let existing = recover(handoff);
    if (currentGeneration().closed) return null;
    existing ||= upgradeJourney(null, generation.id);
    try {
      const location = getLocation();
      const marketing = isMarketingLocation(location);
      const page = marketing ? marketingPageCode(location.pathname) : null;
      const shouldCapture = capture && isAcquisitionPage(location.pathname);
      let current = shouldCapture ? currentTouch(existing, Boolean(handoff)) : null;
      // An internal arrival with no surviving context is unknown, not a fabricated referral.
      if (shouldCapture && page && !current && !existing.arrivals.length) {
        current = validateTouch({ source: "unknown", medium: "unknown", site: "m", page_code: page,
          captured_at: new Date(now()).toISOString() }, now());
        markIncomplete(existing, "summary_only");
      }
      if (current) {
        existing.first_touch ||= current;
        existing.signup_touch ||= current;
        if (isNonDirect(current)) existing.signup_touch = current;
        const id = nextObservationId(existing);
        existing.arrivals.push({ id, site: marketing ? "m" : "i", touch: current, pages: [] });
        existing.active_arrival = id;
        existing.last_observation = id;
        activeArrival = id;
      } else activeArrival ||= existing.active_arrival || existing.arrivals.at(-1)?.id;
      if (shouldCapture && page) {
        const arrival = existing.arrivals.find(a => a.id === activeArrival);
        if (arrival) {
          const id = nextObservationId(existing);
          arrival.pages.push([id, page]);
          existing.last_observation = id;
        }
      }
    } catch { /* Keep recovered evidence if optional capture fails. */ }
    const result = validate(existing);
    if (result) persist(result);
    return validate(memory || result);
  }
  function forSubmission(record) {
    const safe = validate(record);
    const marker = currentGeneration(safe);
    if (marker.closed || (safe?.version === 2 && safe.generation !== marker.id)) return null;
    const latest = recover();
    return mergeJourneys([safe, latest], marker.id, now());
  }
  return { read, readSession, readHandoff, recover, persist, clear, prepare, validate, forSubmission };
}

export const acquisitionAttribution = createAttributionService();
export const readAttributionCookie = acquisitionAttribution.read;
export const clearAttributionCookie = () => acquisitionAttribution.clear("withdrawal");
export const prepareSignupAttribution = acquisitionAttribution.prepare;
